import { computed, ref, watch, type Ref } from "vue";
import type DSTitleFieldDisplay from "@/src/design_system/form_elements/title_field/components/DSTitleFieldDisplay.vue";
import type DSTitleFieldEditor from "@/src/design_system/form_elements/title_field/components/DSTitleFieldEditor.vue";
import { useDSTitleFieldErrors } from "@/src/design_system/form_elements/title_field/composables/useDSTitleFieldErrors";
import { DS_TITLE_FIELD_CLASSES } from "@/src/design_system/form_elements/title_field/constants/classes";
import type { DSTitleFieldEmits, DSTitleFieldProps } from "@/src/design_system/form_elements/title_field/types";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";

interface UseDSTitleFieldOptions {
  props: Readonly<DSTitleFieldProps>;
  emit: DSTitleFieldEmits;
  displayControlRef: Ref<InstanceType<typeof DSTitleFieldDisplay> | undefined>;
  editorRef: Ref<InstanceType<typeof DSTitleFieldEditor> | undefined>;
}

export const useDSTitleField = ({ props, emit, displayControlRef, editorRef }: UseDSTitleFieldOptions) => {
  const uncontrolledEditing = ref(Boolean(props.defaultEditing) && !props.disabled);
  const { errorMessageIdPrefix, errorMessageIds, hasErrors } = useDSTitleFieldErrors(props);
  let shouldRestoreDisplayFocus = false;

  const editing = computed(() => (props.editing === undefined ? uncontrolledEditing.value : props.editing));
  const isEditing = computed(() => editing.value && !props.disabled);
  const activeControl = computed(() => (isEditing.value ? editorRef.value : displayControlRef.value));
  const rootClasses = computed(() => ({
    [DS_TITLE_FIELD_CLASSES.Root]: true,
    [DS_TITLE_FIELD_CLASSES.Editing]: isEditing.value,
    [DS_TITLE_FIELD_CLASSES.Empty]: !props.modelValue && !props.placeholder,
    [DS_STATE_CLASSES.Disabled]: props.disabled,
    [DS_STATE_CLASSES.Error]: hasErrors.value,
  }));

  function setEditing(value: boolean) {
    if (editing.value === value) return;

    if (props.editing === undefined) uncontrolledEditing.value = value;
    emit("update:editing", value);
  }

  function startEditing() {
    if (props.disabled || props.loading || editing.value) return;

    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    emit("cancel");
  }

  // Capture focus before the editor is removed and the browser focuses the body.
  watch(editing, () => {
    shouldRestoreDisplayFocus = Boolean(editorRef.value?.containsFocus());
  });

  watch(
    editing,
    (value, wasEditing) => {
      if (!value && wasEditing && shouldRestoreDisplayFocus && !props.disabled) displayControlRef.value?.focus();
    },
    { flush: "post" }
  );

  watch(
    () => props.disabled,
    disabled => {
      if (!disabled || !editing.value) return;

      setEditing(false);
    }
  );

  return {
    activeControl,
    cancelEditing,
    errorMessageIdPrefix,
    errorMessageIds,
    hasErrors,
    isEditing,
    rootClasses,
    startEditing,
  };
};
